'use client';

import { ClockIcon, PencilIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { Schedule } from '@/types';
import { formatDateForUser } from '@/utils/formatters';
import {
  knockoutBlockLabel,
  knockoutDayLabel,
  knockoutStartOf,
  type KnockoutStart,
} from '@/utils/tournament-schedule';

import { CollapsibleCard } from './CollapsibleCard';
import { useSummaryCollapse } from './useSummaryCollapse';

interface TournamentDetailsScheduleCardProps {
  schedule: Schedule[] | undefined;
  onEditScheduleClick?: () => void;
  /** Resolved by the page: the schedule is editable while no fixture exists. */
  canEdit: boolean;
  isEditMode?: boolean;
  /** Folded away while the tournament is in progress; the fixture takes the screen. */
  shouldCollapse?: boolean;
  /** SHORT with a bracket on fixed days: tags the blocks and days where it is played. */
  showKnockouts?: boolean;
  /** The tournament's cut (the earliest among its blocks); null keeps the whole last day. */
  knockoutStart?: KnockoutStart | null;
}

export function TournamentDetailsScheduleCard({
  schedule,
  onEditScheduleClick,
  canEdit,
  isEditMode = false,
  shouldCollapse = false,
  showKnockouts = false,
  knockoutStart = null,
}: TournamentDetailsScheduleCardProps) {
  const [collapsed, setCollapsed] = useSummaryCollapse(shouldCollapse);

  const dates = [
    ...new Set(
      (schedule ?? []).flatMap((block) => block.schedule.map((d) => d.date)),
    ),
  ].sort();
  // Without a cut the bracket takes the whole last day, unless a single day is shared by both phases.
  const finalDay =
    showKnockouts && !knockoutStart && dates.length > 1
      ? dates[dates.length - 1]
      : null;

  return (
    <CollapsibleCard
      title="Horarios"
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      // Tighter header only while open: folded, it has to match the other cards.
      headerClassName={collapsed ? undefined : 'pb-2'}
      actions={
        isEditMode && canEdit && onEditScheduleClick ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onEditScheduleClick}
            className="h-8 w-8 p-0"
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
        ) : null
      }
    >
      {schedule && schedule.length > 0 ? (
        <div className="space-y-6">
          {schedule.map((scheduleItem, scheduleIndex) => {
            const blockName =
              scheduleItem.name?.trim() || `Horario ${scheduleIndex + 1}`;
            const knockoutLabel = showKnockouts
              ? knockoutBlockLabel(
                  knockoutStartOf(scheduleItem),
                  scheduleItem.schedule.flatMap((d) =>
                    d.timeRange.map((r) => ({
                      date: d.date,
                      startTime: r.startTime,
                    })),
                  ),
                  finalDay,
                )
              : null;
            return (
              <div key={`schedule-${scheduleIndex}`} className="space-y-4">
                <div className="space-y-0.5 border-b border-gray-100 pb-3">
                  <h3 className="font-semibold">{scheduleItem.club.name}</h3>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="text-sm font-medium text-gray-700">
                      {blockName}
                    </p>
                    {knockoutLabel && (
                      <Badge
                        variant="outline"
                        className="border-amber-500 text-xs text-amber-700"
                      >
                        {knockoutLabel}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  {[...scheduleItem.schedule]
                    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''))
                    .map((daySchedule, dayIndex) => {
                      const dayKnockoutLabel = showKnockouts
                        ? knockoutDayLabel(
                            daySchedule.date,
                            daySchedule.timeRange.map((r) => r.startTime),
                            finalDay,
                            knockoutStart,
                          )
                        : null;
                      return (
                        <div
                          key={`day-${scheduleIndex}-${dayIndex}`}
                          className="rounded-lg border p-4"
                        >
                          <div className="mb-3 flex flex-wrap items-center gap-2">
                            <h4 className="font-medium">
                              {formatDateForUser(daySchedule.date)}
                            </h4>
                            {dayKnockoutLabel && (
                              <Badge
                                variant="outline"
                                className="border-amber-500 text-xs text-amber-700"
                              >
                                {dayKnockoutLabel}
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                            {daySchedule.timeRange.map(
                              (timeSlot, timeIndex) => (
                                <div
                                  key={`time-${scheduleIndex}-${dayIndex}-${timeIndex}`}
                                  className="flex justify-between rounded border p-2 text-sm"
                                >
                                  <span>
                                    {timeSlot.startTime.substring(0, 5)} -{' '}
                                    {timeSlot.endTime.substring(0, 5)}
                                  </span>
                                  <span className="font-medium">
                                    {timeSlot.courtsQuantity}{' '}
                                    {timeSlot.courtsQuantity === 1
                                      ? 'cancha'
                                      : 'canchas'}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="py-8 text-center">
          <ClockIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <h3 className="text-lg font-medium">Horarios no disponibles</h3>
          <p className="text-gray-500">
            No hay horarios definidos para este torneo.
          </p>
          {isEditMode && canEdit && onEditScheduleClick && (
            <Button
              variant="outline"
              onClick={onEditScheduleClick}
              className="mt-4 bg-transparent"
            >
              <PencilIcon className="mr-2 h-4 w-4" />
              Configurar Horarios
            </Button>
          )}
        </div>
      )}
    </CollapsibleCard>
  );
}
